import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { API_BASE, api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useDebouncedValue } from "@/lib/use-debounced-value";
import { track } from "@/lib/analytics";
import { fmtCompactNum } from "@/components/athlete/formatters";
import { ArrowRight } from "lucide-react";
import "./home.css";
import { FlipWords } from "@/components/ui/flip-words";
import { SeriesCollectionGrid } from "@/components/SeriesCollectionGrid";

type HomeStats = {
  totalResults: number;
  totalEvents: number;
  totalAthletes: number;
  totalCountries: number;
};

type SearchType = "athletes" | "events";

const QUICK_SEARCHES: Record<SearchType, string[]> = {
  athletes: ["Jan Frodeno", "Daniela Ryf", "Eliud Kipchoge"],
  events: ["IRONMAN World Championship", "Boston Marathon", "Berlin Marathon"],
};

const SEARCH_CONTENT: Record<
  SearchType,
  { placeholder: string; singular: string }
> = {
  athletes: {
    placeholder: "Search athletes by name or hometown",
    singular: "athlete",
  },
  events: {
    placeholder: "Search events or race series",
    singular: "event",
  },
};

const FEATURES = [
  {
    number: "01",
    title: "Search & Discover",
    description:
      "Find any athlete or race across our database of endurance events. Cross-reference results, locations, and years.",
  },
  {
    number: "02",
    title: "Performance Analytics",
    description:
      "Track split times, race-over-race progression, and age-group rankings for every recorded finish.",
  },
  {
    number: "03",
    title: "Event Intelligence",
    description:
      "Explore participation trends, demographic breakdowns, and historical data across thousands of events.",
  },
];

function RevealSection({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLElement>) {
  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setInView(true);
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className={cn("home-reveal", inView && "in-view", className)}
      {...props}
    >
      {children}
    </section>
  );
}

export default function LandingPage() {
  const [query, setQuery] = useState("");
  const [searchType, setSearchType] = useState<SearchType>("athletes");
  const [revealed, setRevealed] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterState, setNewsletterState] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");
  const [newsletterError, setNewsletterError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  const debouncedQuery = useDebouncedValue(query, 300);
  const isSearching = debouncedQuery.length >= 2;
  const content = SEARCH_CONTENT[searchType];

  const words = ["Athletes", "Results", "Events"];

  useEffect(() => {
    requestAnimationFrame(() => setRevealed(true));
  }, []);

  const athleteSearch = useQuery({
    queryKey: ["search", "athletes", debouncedQuery],
    queryFn: async () => {
      const { data, error } = await api.GET("/search/athletes", {
        params: { query: { limit: 10, offset: 0, q: debouncedQuery } },
      });
      if (error) throw error;
      return data;
    },
    enabled: searchType === "athletes" && isSearching,
  });

  const eventSearch = useQuery({
    queryKey: ["search", "events", debouncedQuery],
    queryFn: async () => {
      const { data, error } = await api.GET("/search/events", {
        params: {
          query: { limit: 10, offset: 0, q: debouncedQuery },
        },
      });
      if (error) throw error;
      return data;
    },
    enabled: searchType === "events" && isSearching,
  });

  const platformStats = useQuery({
    queryKey: ["search", "stats"],
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/search/stats`);
      if (!response.ok) throw new Error("Failed to load platform stats");
      return (await response.json()) as HomeStats;
    },
  });

  const results = searchType === "athletes" ? athleteSearch : eventSearch;
  const resultCount = results.data?.length ?? 0;
  const stat = (v: number | undefined) => (v == null ? "—" : fmtCompactNum(v));

  const handleQuickSearch = (value: string) => {
    track("tr_home_quick_search_click", {
      query_text: value,
      search_type: searchType,
    });
    setQuery(value);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    track("tr_home_search_submit", {
      had_query: query.trim().length > 0,
      query_length: query.length,
      query_text: query,
      search_type: searchType,
    });
    navigate({
      search: {
        q: query || undefined,
        type: searchType !== "athletes" ? "events" : undefined,
      },
      to: "/records",
    });
  };

  const handleNewsletterSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (newsletterState === "submitting" || !newsletterEmail.trim()) return;

    setNewsletterState("submitting");
    setNewsletterError(null);

    track("tr_newsletter_submit_attempt", {
      has_email: Boolean(newsletterEmail.trim()),
      source: "homepage",
    });

    try {
      const response = await fetch(`${API_BASE}/newsletter/subscribe`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: newsletterEmail.trim() }),
      });

      const payload = (await response.json().catch(() => null)) as {
        success?: boolean;
        message?: string;
      } | null;

      if (!response.ok || !payload?.success) {
        throw new Error(
          payload?.message || "Could not subscribe. Please try again.",
        );
      }

      setNewsletterState("success");
      setNewsletterEmail("");
      track("tr_newsletter_submit_success", { source: "homepage" });
    } catch (error) {
      setNewsletterState("error");
      setNewsletterError(
        error instanceof Error ? error.message : "Could not subscribe.",
      );
      track("tr_newsletter_submit_error", {
        reason: error instanceof Error ? error.message : "unknown",
        source: "homepage",
      });
    }
  };

  return (
    <div className="home-page">
      {/* ── Hero (search) ── */}
      <section className={cn("home-hero", revealed && "revealed")}>
        <div className="home-hero-shell">
          <div className="home-kicker">// Endurance Athletics Database</div>
          <h1 className="home-title">
            Search{" "}
            <strong>
              <FlipWords className="text-primary-400!" words={words} />
            </strong>
          </h1>
          <p className="home-subtitle">
            Search athletes and race results across the endurance events.
          </p>

          <form className="home-workspace" onSubmit={handleSubmit}>
            <div className="home-mode-row" aria-label="Search mode">
              <button
                type="button"
                className={cn(
                  "home-mode-toggle",
                  searchType === "athletes" && "active",
                )}
                aria-pressed={searchType === "athletes"}
                onClick={() => setSearchType("athletes")}
              >
                Athletes
              </button>
              <button
                type="button"
                className={cn(
                  "home-mode-toggle",
                  searchType === "events" && "active",
                )}
                aria-pressed={searchType === "events"}
                onClick={() => setSearchType("events")}
              >
                Events
              </button>
            </div>

            <div className="home-search-field">
              <input
                ref={inputRef}
                id="home-search-input"
                type="text"
                className="home-search-input"
                placeholder={content.placeholder}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              <button className="home-search-submit" type="submit">
                Search
              </button>
            </div>

            <div className="home-chip-row">
              {QUICK_SEARCHES[searchType].map((value) => (
                <button
                  key={value}
                  type="button"
                  className="home-chip"
                  onClick={() => handleQuickSearch(value)}
                >
                  {value}
                </button>
              ))}
            </div>

            {(isSearching || query.length === 1) && (
              <section className="home-results" aria-live="polite">
                {query.length === 1 && (
                  <div className="home-results-message">
                    Type one more character to search.
                  </div>
                )}

                {isSearching && results.isLoading && (
                  <div className="home-results-message">Searching...</div>
                )}

                {isSearching && results.isError && (
                  <div className="home-results-message">
                    Search is temporarily unavailable. Try again shortly.
                  </div>
                )}

                {isSearching && !results.isLoading && !results.isError && (
                  <>
                    <div className="home-results-count">
                      {resultCount} {content.singular}
                      {resultCount !== 1 ? "s" : ""} found
                    </div>

                    <div className="home-results-list">
                      {searchType === "athletes" &&
                        athleteSearch.data?.map((athlete) => (
                          <Link
                            key={athlete.id}
                            to="/athletes/$athleteId"
                            params={{ athleteId: athlete.id }}
                            className="home-result-row"
                            onClick={() =>
                              track("tr_home_preview_result_click", {
                                destination_id: athlete.id,
                                destination_kind: "athlete",
                                preview_name: athlete.fullName,
                                preview_state: "live",
                                query_text: query,
                                search_type: searchType,
                              })
                            }
                          >
                            <div className="home-result-main">
                              <div className="home-result-name">
                                {athlete.fullName}
                              </div>
                              <div className="home-result-meta">
                                {[
                                  athlete.city,
                                  athlete.state,
                                  (
                                    athlete as {
                                      representingCountryIso3?: string;
                                    }
                                  ).representingCountryIso3 ??
                                    athlete.countryIso2,
                                ]
                                  .filter(Boolean)
                                  .join(", ")}
                              </div>
                            </div>
                            <div className="home-result-detail">
                              {athlete.resultCount} race
                              {athlete.resultCount !== 1 ? "s" : ""}
                            </div>
                          </Link>
                        ))}

                      {searchType === "events" &&
                        eventSearch.data?.map((eventItem) =>
                          eventItem.kind === "group" ? (
                            <Link
                              key={eventItem.id}
                              to="/events/group/$groupId"
                              params={{ groupId: eventItem.id }}
                              className="home-result-row"
                              onClick={() =>
                                track("tr_home_preview_result_click", {
                                  destination_id: eventItem.id,
                                  destination_kind: "event_group",
                                  preview_name: eventItem.displayName,
                                  preview_state: "live",
                                  query_text: query,
                                  search_type: searchType,
                                })
                              }
                            >
                              <div className="home-result-main">
                                <div className="home-result-name">
                                  {eventItem.displayName}
                                </div>
                                <div className="home-result-meta">
                                  {[eventItem.brand, "Group"]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </div>
                              </div>
                              <div className="home-result-detail">
                                {eventItem.seriesCount} race
                                {eventItem.seriesCount !== 1 ? "s" : ""}
                              </div>
                            </Link>
                          ) : (
                            <Link
                              key={eventItem.id}
                              to="/events/series/$seriesId"
                              params={{ seriesId: eventItem.id }}
                              search={{ year: undefined }}
                              className="home-result-row"
                              onClick={() =>
                                track("tr_home_preview_result_click", {
                                  destination_id: eventItem.id,
                                  destination_kind: "event_series",
                                  preview_name: eventItem.displayName,
                                  preview_state: "live",
                                  query_text: query,
                                  search_type: searchType,
                                })
                              }
                            >
                              <div className="home-result-main">
                                <div className="home-result-name">
                                  {eventItem.displayName}
                                </div>
                                <div className="home-result-meta">
                                  {[eventItem.brand, eventItem.raceType]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </div>
                              </div>
                              <div className="home-result-detail">
                                {eventItem.totalResults} result
                                {eventItem.totalResults !== 1 ? "s" : ""}
                              </div>
                            </Link>
                          ),
                        )}

                      {resultCount === 0 && (
                        <div className="home-results-message">
                          No {searchType} found for "{query}".
                          {searchType === "events" ? (
                            <Link
                              to="/request-race"
                              search={{
                                race: query || undefined,
                                source: "home",
                              }}
                              className="home-missing-race-link"
                              onClick={() =>
                                track("tr_race_request_cta_click", {
                                  source: "home",
                                  query_text: query,
                                  search_type: searchType,
                                })
                              }
                            >
                              Missing a race? Request it
                            </Link>
                          ) : null}
                        </div>
                      )}

                      {resultCount >= 10 && (
                        <Link
                          to="/records"
                          search={{
                            q: query || undefined,
                            type:
                              searchType !== "athletes" ? "events" : undefined,
                          }}
                          className="home-results-show-more"
                          onClick={() =>
                            track("tr_home_show_more_click", {
                              query_text: query,
                              search_type: searchType,
                              result_count: resultCount,
                            })
                          }
                        >
                          Show more results
                          <ArrowRight size={14} />
                        </Link>
                      )}
                    </div>
                  </>
                )}
              </section>
            )}
          </form>

          <SeriesCollectionGrid />
        </div>
      </section>

      {/* ── Stats strip ── */}
      <RevealSection className="home-stats-strip" aria-label="Platform totals">
        <div className="home-stats-inner">
          {[
            {
              value: stat(platformStats.data?.totalResults),
              label: "Results",
            },
            {
              value: stat(platformStats.data?.totalAthletes),
              label: "Athletes",
            },
            {
              value: stat(platformStats.data?.totalEvents),
              label: "Races",
            },
          ].map(({ value, label }) => (
            <div key={label} className="home-stat">
              <span className="home-stat-value">{value}</span>
              <span className="home-stat-label">{label}</span>
            </div>
          ))}
        </div>
      </RevealSection>

      {/* ── Features ── */}
      <RevealSection className="home-features">
        <div className="home-section-container">
          <div className="home-section-header">
            <span className="home-section-label">// Capabilities</span>
            <h2 className="home-section-heading">
              Explore endurance athletics like never before
            </h2>
          </div>
          <div className="home-features-grid">
            {FEATURES.map((feature) => (
              <div key={feature.number} className="home-feature">
                <span className="home-feature-number">{feature.number}</span>
                <h3 className="home-feature-title">{feature.title}</h3>
                <p className="home-feature-description">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </RevealSection>

      {/* ── CTA ── */}
      <RevealSection className="home-cta">
        <div className="home-cta-inner">
          <span className="home-section-label">// Get started</span>
          <h2 className="home-cta-heading">Ready to explore the data?</h2>
          <p className="home-cta-text">
            Browse millions of results across thousands of endurance events
            worldwide.
          </p>
          <Link
            to="/records"
            search={{}}
            className="home-cta-button"
            onClick={() =>
              track("tr_home_cta_browse_click", { source: "cta_section" })
            }
          >
            Browse Records
            <ArrowRight className="size-4" />
          </Link>
        </div>
      </RevealSection>

      {/* ── Newsletter ── */}
      <RevealSection className="home-newsletter-section" id="newsletter">
        <div className="home-section-container">
          <div className="home-newsletter-layout">
            <div className="home-newsletter-copy">
              <span className="home-section-label">// Newsletter</span>
              <h2 className="home-newsletter-heading">
                Triathlon Data Insights
              </h2>
              <p className="home-newsletter-text">
                Highlights of interesting endurance discussions, curious
                statistics, and race history.
              </p>
            </div>

            <div className="home-newsletter-form-wrap">
              {newsletterState === "success" ? (
                <div className="home-newsletter-ack" aria-live="polite">
                  You&apos;re subscribed! Check your inbox for a welcome email.
                </div>
              ) : (
                <form
                  className="home-newsletter-form"
                  onSubmit={handleNewsletterSubmit}
                >
                  <label
                    className="home-newsletter-label"
                    htmlFor="newsletter-email"
                  >
                    Email address
                  </label>
                  <div className="home-newsletter-field">
                    <input
                      id="newsletter-email"
                      type="email"
                      className="home-newsletter-input"
                      placeholder="you@example.com"
                      value={newsletterEmail}
                      onChange={(event) =>
                        setNewsletterEmail(event.target.value)
                      }
                      required
                    />
                    <button
                      className="home-newsletter-submit"
                      type="submit"
                      disabled={newsletterState === "submitting"}
                    >
                      {newsletterState === "submitting" ? "..." : "Subscribe"}
                      {newsletterState !== "submitting" && (
                        <ArrowRight className="size-3.5" />
                      )}
                    </button>
                  </div>
                  {newsletterError && (
                    <p className="home-newsletter-error">{newsletterError}</p>
                  )}
                  <p className="home-newsletter-disclaimer">
                    No spam, ever. Unsubscribe at any time.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </RevealSection>
    </div>
  );
}
